import { setInvoiceDetailsData } from '../../../redux/invoiceDetailsDataSlice';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { CustomErrorLogger } from '../../../utils/CustomErrorLogger';
import { useHasPermission } from '../../../hooks/useHasPermission';
import { EditInvoiceLine } from '../editInvoice/editInvoiceLine';
import { Box, Typography, Grid, Divider } from '@mui/material';
import { AddInvoiceLine } from '../editInvoice/addInvoiceLine';
import { useSelector, useDispatch } from 'react-redux';
import { DeleteRecord } from '../../deleteRecord';
import NullDataBox from '../../nullDataBox';
import React, { useState } from 'react';
import Iconify from '../../Iconify';
import axios from 'axios';
import '../../../css/invoices/invoiceLines.css';

const InvoiceLines = () => {

    const dispatch = useDispatch();
    const invoiceDetailsData = useSelector((state) => state.InvoiceDetailsData);
    const hasInvoiceUpdatePermission = useHasPermission('facture_update');
    const totalAmount = invoiceDetailsData?.invoice_detail?.reduce((sum, line) => sum + line.amount, 0) || 0;

    const [openAddLine, setOpenAddLine] = useState(false);
    const [lineToDelete, setLineToDelete] = useState(null);
    const [openDeleteLine, setOpenDeleteLine] = useState(false);
    const [editLine, setEditLine] = useState({ line_id: null, open: false });

    const openDeleteLineDialog = (lineId) => {
        setLineToDelete(lineId);
        setOpenDeleteLine(true);
    }

    const handleDeleteLine = async () => {
        try {
            const response = await axios.delete(`invoice-detail/${lineToDelete}`);
            if (response.status === 200 || response.status === 201) {
                setOpenDeleteLine(false);
                dispatch(setInvoiceDetailsData({
                    ...invoiceDetailsData,
                    invoice_detail: invoiceDetailsData.invoice_detail.filter(
                        (detail) => detail.id !== lineToDelete
                    ),
                }));
            }
        } catch (error) {
            console.error('Error deleting invoice line:', error);
            CustomErrorLogger(error, "Delete Invoice line");
        }
    }

    const updateLinesOrder = async (linesData) => {
        try {
            await axios.put(`invoice-detail/updateOrder`, linesData);
        } catch (error) {
            console.error('Failed to update order:', error);
            CustomErrorLogger(error, 'Failed to update lines order');
        }
    };

    const onDragEndLine = (result) => {
        if (!result.destination) {
            return; // Exit if the item is dropped outside a valid destination
        }

        // Create a copy of the current invoice_detail array
        const newInvoiceDetails = Array.from(invoiceDetailsData.invoice_detail);

        // Remove the dragged line from its original position
        const [movedLine] = newInvoiceDetails.splice(result.source.index, 1);

        // Insert the dragged line into its new position
        newInvoiceDetails.splice(result.destination.index, 0, movedLine);

        // Update the order property of each line based on its new position
        const updatedInvoiceDetails = newInvoiceDetails.map((line, index) => ({
            ...line,
            order: index + 1, // Set the order to match the new index
        }));

        // Dispatch the updated invoiceDetailsData to the Redux store
        dispatch(setInvoiceDetailsData({
            ...invoiceDetailsData,
            invoice_detail: updatedInvoiceDetails,
        }));

        updateLinesOrder(updatedInvoiceDetails.map(line => line.id));
    };

    return (
        <Box className='invoiceLines_box'>
            <Box className='invoiceLines_edit_box'>
                <Typography className='invoiceLines_title'>Lignes de facture</Typography>
                {(hasInvoiceUpdatePermission && invoiceDetailsData?.invoice_detail?.length > 0) && <Typography className='invoiceLines_line_ajouter' onClick={() => setOpenAddLine(true)}>+ Ajouter une ligne</Typography>}
            </Box>

            {invoiceDetailsData?.invoice_detail?.length === 0 ?
                <NullDataBox
                    setOpenDialog={setOpenAddLine}
                    buttonTitle="Ajouter lignes de facture"
                    description="Vous avez ajouté vos machines. Vous pouvez maintenant procéder à la création des lignes de facture associées"
                />
                :
                <Box className='invoiceLines_details_main_box'>
                    <Typography id="outlined-weight-helper-text" className="invoiceLines_line_hint"> <Iconify className='invoiceLines_info_icon' icon={"material-symbols:info-outline"} /> Les montant sons TTC (Toutes Taxes Comprises)</Typography>
                    <DragDropContext onDragEnd={onDragEndLine}>
                        <Droppable droppableId="droppable">
                            {(provided) => (
                                <div {...provided.droppableProps} ref={provided.innerRef} className='invoiceLines_details_actions_box'>
                                    {invoiceDetailsData?.invoice_detail?.map((line, index) => (
                                        <Draggable key={line.id} draggableId={line.id.toString()} index={index}>
                                            {(provided) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    className='invoiceLines_details_oneLine_box'
                                                >
                                                    {hasInvoiceUpdatePermission && (
                                                        <div {...provided.dragHandleProps}>
                                                            <Iconify
                                                                className='invoiceLines_order_icon'
                                                                icon={"fluent:re-order-dots-vertical-16-filled"}
                                                            />
                                                        </div>
                                                    )}
                                                    <Grid container>
                                                        <Grid item xs={12} sm={6} md={5} className='invoiceLines_icon_title_response_box'>
                                                            <Iconify icon={"pepicons-pencil:file"} width="32px" />
                                                            <Box className='invoiceLines_response_box'>
                                                                <Typography className='invoiceLines_text_label'>Description :</Typography>
                                                                <Typography className='invoiceLines_text_value'>{line?.description}</Typography>
                                                            </Box>
                                                        </Grid>
                                                        <Grid item xs={12} sm={6} md={3} className='invoiceLines_icon_title_response_box'>
                                                            <Iconify icon={"material-symbols:euro"} width="32px" />
                                                            <Box className='invoiceLines_response_box'>
                                                                <Typography className='invoiceLines_text_label'>Montant :</Typography>
                                                                <Typography className='invoiceLines_text_value'>{new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(line.amount)}</Typography>
                                                            </Box>
                                                        </Grid>
                                                        <Grid item xs={12} sm={6} md={4} className='invoiceLines_icon_title_response_box'>
                                                            <Iconify icon={"material-symbols-light:build-outline"} width="32px" />
                                                            <Box className='invoiceLines_response_box'>
                                                                <Typography className='invoiceLines_text_label'>Machine :</Typography>
                                                                {
                                                                    line?.invoice_detail_machine?.length > 1
                                                                        ?
                                                                        <Box className='invoiceLines_text_value'>
                                                                            <Box className="ships_box">
                                                                                <Typography className="ships_label">Tous les machines  concernées</Typography>
                                                                            </Box>
                                                                        </Box>
                                                                        :
                                                                        <Box className='invoiceLines_line_machines_box'>
                                                                            {line?.invoice_detail_machine?.map((machine) => {
                                                                                return (
                                                                                    <Box className="ships_box">
                                                                                        <Typography className="ships_label">{machine?.machine?.name}</Typography>
                                                                                    </Box>
                                                                                )
                                                                            })}
                                                                        </Box>
                                                                }
                                                            </Box>
                                                        </Grid>
                                                    </Grid>

                                                    {hasInvoiceUpdatePermission && (
                                                        <Box className='invoiceLines_line_actions_box'>
                                                            <Iconify
                                                                className='invoiceLines_action_icon'
                                                                icon={"ic:outline-delete"}
                                                                onClick={() => openDeleteLineDialog(line.id)}
                                                            />
                                                            <Iconify
                                                                className='invoiceLines_action_icon'
                                                                icon={"ic:outline-edit"}
                                                                onClick={() => setEditLine({ line_id: line.id, open: true })}
                                                            />
                                                        </Box>
                                                    )}
                                                </div>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                    <Divider />
                    <Box className='addInvoice_total_box'>
                        <Typography className='addInvoice_total_typo'>Totale</Typography>
                        <Typography className='invoiceLines_total_value'>{new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(totalAmount)}</Typography>
                    </Box>

                </Box>
            }
            {openAddLine &&
                <AddInvoiceLine setOpenAddLine={setOpenAddLine} openAddLine={openAddLine} />
            }
            {editLine.open &&
                <EditInvoiceLine editLine={editLine} setEditLine={setEditLine} />
            }
            {openDeleteLine &&
                <DeleteRecord
                    openDelete={openDeleteLine}
                    setOpenDelete={setOpenDeleteLine}
                    handleDelete={handleDeleteLine}
                    title="Êtes-vous sûr du vouloir supprimer ce ligne ?"
                />
            }

        </Box>

    );
}

export default InvoiceLines;